import { useState } from 'react';
import type { CellRowLookups } from '@erp/item-master-rules';
import type { PendingEditsByRowKey } from './as400ExportEditState';
import type { DivisionOption } from './useDivisionSelection';
import {
  itemAddLookupFor,
  sameItemAddInputs,
  worksheetLookupInputs,
  type ItemAddLookupInputs,
} from './itemAddPresence';
import {
  gridStockUoms,
  itemSheetLookupGate,
  stockUomLookupFor,
} from './itemStockUom';

function useLookupFromInputs<T>(
  inputs: ItemAddLookupInputs,
  lookupFor: (inputs: ItemAddLookupInputs) => T,
): T {
  const [state, setState] = useState(() => ({
    inputs,
    lookup: lookupFor(inputs),
  }));
  if (!sameItemAddInputs(state.inputs, inputs)) {
    setState({ inputs, lookup: lookupFor(inputs) });
  }
  return sameItemAddInputs(state.inputs, inputs)
    ? state.lookup
    : lookupFor(inputs);
}

function useStableLookups(lookups: CellRowLookups): CellRowLookups {
  const [held, setHeld] = useState(lookups);
  if (
    held.itemAdds !== lookups.itemAdds ||
    held.stockUoms !== lookups.stockUoms
  ) {
    setHeld(lookups);
  }
  return held.itemAdds === lookups.itemAdds &&
    held.stockUoms === lookups.stockUoms
    ? held
    : lookups;
}

export function useAs400CellRowLookups(input: {
  pendingByScope: ReadonlyMap<string, PendingEditsByRowKey>;
  itemAddReady: boolean;
  itemAddRows?: ItemAddLookupInputs['rows'];
  itemAddNewRows?: ItemAddLookupInputs['newRows'];
  itemAddScopePrefix?: string;
  itemAddDivisions?: readonly DivisionOption[];
  itemSheetReady: boolean;
  itemSheetRows?: ItemAddLookupInputs['rows'];
  itemSheetNewRows?: ItemAddLookupInputs['newRows'];
  itemSheetScopePrefix?: string;
  itemSheetError: Error | null;
  stockUomLookupRequired: boolean;
}): {
  lookups: CellRowLookups;
  gridLookups: CellRowLookups;
  itemSheetGate: ReturnType<typeof itemSheetLookupGate>;
} {
  const itemAdds = useLookupFromInputs(
    worksheetLookupInputs(
      input.itemAddReady,
      input.itemAddRows,
      input.itemAddNewRows,
      input.itemAddScopePrefix,
      input.pendingByScope,
      input.itemAddDivisions,
    ),
    itemAddLookupFor,
  );
  const stockUoms = useLookupFromInputs(
    worksheetLookupInputs(
      input.itemSheetReady,
      input.itemSheetRows,
      input.itemSheetNewRows,
      input.itemSheetScopePrefix,
      input.pendingByScope,
    ),
    stockUomLookupFor,
  );
  const lookups = useStableLookups({ itemAdds, stockUoms });
  const gridLookups = useStableLookups({
    itemAdds,
    stockUoms: gridStockUoms(stockUoms, input.stockUomLookupRequired),
  });
  return {
    lookups,
    gridLookups,
    itemSheetGate: itemSheetLookupGate(
      input.stockUomLookupRequired,
      input.itemSheetReady,
      input.itemSheetError,
    ),
  };
}
